import type { ReactNode } from "react";

import { useLingui } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import {
  CheckCircle2,
  CreditCard,
  LayoutGrid,
  Receipt,
  SlidersHorizontal,
  Wallet,
} from "lucide-react";

import type { CheckoutStep } from "./checkout-types";

// Legacy source: src/components/billing/checkout/checkout-stepper.tsx (1:1 port).
// Legacy used next-intl namespace "checkout.stepper" with dynamic key t(step);
// Lingui needs static msgids, so the labels live in a per-step map below.

const STEP_ICONS: Record<string, ReactNode> = {
  plan: <LayoutGrid className="size-4" />,
  configure: <SlidersHorizontal className="size-4" />,
  billing: <Receipt className="size-4" />,
  card: <Wallet className="size-4" />,
  pay: <CreditCard className="size-4" />,
};

interface CheckoutStepperProps {
  steps: CheckoutStep[];
  currentStep: CheckoutStep;
  onStepClick: (step: CheckoutStep) => void;
}

export function CheckoutStepper({ steps, currentStep, onStepClick }: CheckoutStepperProps) {
  const { t } = useLingui();
  const currentIdx = steps.indexOf(currentStep);

  // Legacy keys: checkout.stepper.{plan,configure,billing,card,pay}
  const stepLabels: Record<string, string> = {
    plan: t`Plan`,
    configure: t`Configure`,
    billing: t`Billing`,
    card: t`Payment`,
    pay: t`Confirm`,
  };

  return (
    <div className="flex items-center justify-center gap-1.5 px-5 py-3 sm:gap-2">
      {steps.map((step, i) => {
        const isCompleted = i < currentIdx;
        const isCurrent = i === currentIdx;
        const canClick = isCompleted;

        return (
          <div key={step} className="flex items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              disabled={!canClick}
              onClick={() => canClick && onStepClick(step)}
              className={cn(
                "flex size-7 items-center justify-center rounded-full text-xs font-medium transition-colors sm:size-8",
                isCompleted && "bg-primary/15 text-primary hover:bg-primary/25 cursor-pointer",
                isCurrent && "bg-primary text-primary-foreground",
                !isCompleted && !isCurrent && "bg-muted text-muted-foreground cursor-default",
              )}
              title={canClick ? stepLabels[step] : undefined}
            >
              {isCompleted ? <CheckCircle2 className="size-3.5 sm:size-4" /> : STEP_ICONS[step]}
            </button>
            {/* Label — hidden on small mobile */}
            <span
              className={cn(
                "hidden text-xs font-medium sm:inline",
                isCurrent
                  ? "text-foreground font-semibold"
                  : isCompleted
                    ? "text-primary"
                    : "text-muted-foreground",
              )}
            >
              {stepLabels[step]}
            </span>
            {/* Connecting line */}
            {i < steps.length - 1 && (
              <div
                className={cn(
                  "h-px w-4 transition-colors sm:w-8",
                  isCompleted ? "bg-primary" : "bg-border",
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
